import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { ExtensionTypes } from 'generated/graphql-types';
import { take } from 'rxjs/operators';
import { extensionsRoute } from 'src/app/app-routing.module';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { getAccount } from '../../account-details/state/account-detail.selectors';
import { ExtensionPrivileges } from '../../extension-details/models/extension-privileges';
import { ExtensionOrderingOptions, ExtensionRegisterOptions } from '../models/extension-register-options';
import { ExtensionRegisterListItem } from '../models/extension-register-results.model';
import { ExtensionTypeOption } from '../models/extension-type-option.interface';
import { PhonePrivileges } from '../../phones/models/phone-privileges';
import { selectQuickFilters } from '../state/extension-register.reducer';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-extension-register-view',
    styleUrls: ['./extension-register-view.component.scss'],
    templateUrl: './extension-register-view.component.html',
    standalone: false
})
export class ExtensionRegisterViewComponent extends EntityRegisterView<
   ExtensionRegisterListItem,
   ExtensionRegisterOptions,
   ExtensionOrderingOptions
> {
   protected selectQuickFilters(): (state: object) => ExtensionRegisterOptions | null {
      return selectQuickFilters;
   }

   protected override onQuickFiltersInit(quickFilters: ExtensionRegisterOptions): void {
      if (quickFilters) {
         this.selectedExtensionType = quickFilters.extensionType;
         this.showExtensionsWithIssues = quickFilters.showExtensionsWithIssues;
      }
   }
   @Input() extensionPrivileges: ExtensionPrivileges;

   @Input() phonePrivileges: PhonePrivileges;

   @Input() accountId: number | null;

   @Input() showExtensionRegisterPopup: boolean;

   @Input() set isSystemUser(value: boolean | null) {
      if (value != null) {
         this._isSystemUser = value;
         this.updateAvailableExtensionOptions();
      }
   }
   get isSystemUser() {
      return this._isSystemUser;
   }

   @Output() resetHackedAndFraud = new EventEmitter<ExtensionRegisterListItem>();

   showExtensionsWithIssues: boolean = false;

   extensionTypes: typeof ExtensionTypes = ExtensionTypes;

   @Input() set createExtensionTypeOptions(options: ExtensionTypeOption[] | null) {
      if (options && options.length > 0) {
         this._rawExtensionTypeOptions = options;
         this.updateAvailableExtensionOptions();
      }
   }

   get createExtensionTypeOptions() {
      return this._createExtensionTypeOptions;
   }

   @Output() createExtensionTypeChange = new EventEmitter<ExtensionTypeOption>();

   selectedExtensionType: ExtensionTypeOption | undefined = undefined;

   selectedCreateExtensionType: ExtensionTypeOption | undefined = undefined;

   private _isSystemUser: boolean = false;

   private _rawExtensionTypeOptions: ExtensionTypeOption[] = [];

   private _createExtensionTypeOptions: ExtensionTypeOption[] = [];

   public canAddExtensions(): boolean {
      if (!this.createExtensionTypeOptions) {
         return false;
      }
      return this.createExtensionTypeOptions.length > 0;
   }

   getRouterLink(id: number) {
      return `/${extensionsRoute}/${id}`;
   }

   /**
    * Deep-links the registration icons to the extension's Phones tab. That tab is only rendered for
    * system users holding the phone view privilege, and NdtTabRouterDirective redirects to
    * /not-found when the tab in the url does not exist, so fall back to the plain detail link.
    */
   getPhonesTabRouterLink(id: number) {
      const detailLink = this.getRouterLink(id);
      return this.isSystemUser && this.phonePrivileges?.canView ? `${detailLink}/phones` : detailLink;
   }

   onExtensionTypeSelected(extensionType: ExtensionTypeOption) {
      this.registerOptions = { ...this.registerOptions, extensionType: extensionType };
      this.quickFilterChange.emit(this.registerOptions);
   }

   onCreateExtensionTypeSelected(extensionType: ExtensionTypeOption) {
      this.createExtensionTypeChange.emit(extensionType);
   }

   toggleShowExtensionsWithIssues(event) {
      this.registerOptions = {
         ...this.registerOptions,
         showExtensionsWithIssues: event,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   resetHackedAndFraudDevice(extension: ExtensionRegisterListItem) {
      this.resetHackedAndFraud.emit(extension);
   }

   private updateAvailableExtensionOptions(): void {
      if (!this._rawExtensionTypeOptions?.length) return;

      let options = [...this._rawExtensionTypeOptions];
      let finalOptions: ExtensionTypeOption[] = [];

      const typesRequiringPrivileges = new Set([
         ExtensionTypes[ExtensionTypes.Phone],
         ExtensionTypes[ExtensionTypes.AutoAttendant],
         ExtensionTypes[ExtensionTypes.Hangup],
         ExtensionTypes[ExtensionTypes.Directory],
      ]);

      const systemUserOnlyTypes = new Set([
         ExtensionTypes[ExtensionTypes.CallQueue],
         ExtensionTypes[ExtensionTypes.FindMeFollowMe],
         ExtensionTypes[ExtensionTypes.Fax],
      ]);

      options = options.filter(
         (o) =>
            !typesRequiringPrivileges.has(ExtensionTypes[o.name]) && !systemUserOnlyTypes.has(ExtensionTypes[o.name])
      );
      if (this.extensionPrivileges.canAddPhones && this._isSystemUser) {
         const phoneOption = this._rawExtensionTypeOptions.find((x) => x.name === ExtensionTypes[ExtensionTypes.Phone]);
         if (phoneOption) finalOptions.push(phoneOption);
      }

      if (this.extensionPrivileges.canAddAutoAttendants) {
         const autoAttendantOption = this._rawExtensionTypeOptions.find(
            (x) => x.name === ExtensionTypes[ExtensionTypes.AutoAttendant]
         );
         if (autoAttendantOption) finalOptions.push(autoAttendantOption);
      }

      if (this.extensionPrivileges.canAddFaxes && this._isSystemUser) {
         const faxOption = this._rawExtensionTypeOptions.find((x) => x.name === ExtensionTypes[ExtensionTypes.Fax]);
         if (faxOption) finalOptions.push(faxOption);
      }

      if (this._isSystemUser && this.extensionPrivileges.canAddOthers) {
         const callQueueOption = this._rawExtensionTypeOptions.find(
            (x) => x.name === ExtensionTypes[ExtensionTypes.CallQueue]
         );
         if (callQueueOption) finalOptions.push(callQueueOption);

         const fmFmOption = this._rawExtensionTypeOptions.find(
            (x) => x.name === ExtensionTypes[ExtensionTypes.FindMeFollowMe]
         );
         if (fmFmOption) finalOptions.push(fmFmOption);
      }

      if (this.extensionPrivileges.canAddOthers) {
         finalOptions.push(...options);
      }

      this.store
         .select(getAccount)
         .pipe(take(1))
         .subscribe((account) => {
            if (!account?.isFmFmEnabled) {
               finalOptions = finalOptions.filter((x) => x.name !== ExtensionTypes[ExtensionTypes.FindMeFollowMe]);
            }

            finalOptions.sort((a, b) => a.displayName.localeCompare(b.displayName));
            this._createExtensionTypeOptions = finalOptions;
         });

      finalOptions = finalOptions.filter((x) => x.name !== ExtensionTypes[ExtensionTypes.SmartVoicemail]);

      finalOptions.sort((a, b) => a.displayName.localeCompare(b.displayName));
      this._createExtensionTypeOptions = finalOptions;
   }
}
